import {
  CreateCampaignCommand,
  CreateCampaignCommandInput,
  CreateCampaignCommandOutput,
  FireTVCampaignAutomationClient,
  GetCampaignCommand,
  GetCampaignCommandOutput,
  GetPresignedUrlCommand,
  GetTransformedAssetCommand,
  GetTransformedAssetCommandOutput,
  PresignedUrlAction,
  SearchCommand,
  SearchInput,
  SearchOutput,
  Status,
  TransformAssetCommand,
  TransformAssetInput,
  TransformAssetOutput,
} from '@amzn/fire-tv-campaign-automation-service';
import { logger } from 'src/logger';

import { withTokenRefresh } from '../withTokenRefresh';

/**
 * Response type for presigned URL operations
 */
export interface PresignedUrlResult {
  key: string;
  presignedUrl: string;
}

/**
 * Core implementation of presigned URL fetching
 */
const getPresignedUrlsBase = async (
  client: FireTVCampaignAutomationClient,
  action: PresignedUrlAction,
  keys: string[],
): Promise<Array<PresignedUrlResult>> => {
  if (!keys.length) return [];
  logger.debug('Fetching presigned URLs from control plane', { action, keys });

  const response = await client.send(
    new GetPresignedUrlCommand({
      action,
      keys,
    }),
  );

  if (response.presignedUrls?.length !== keys.length) {
    logger.error('Failed to fetch presigned URLs', {
      requestedKeys: keys,
      receivedUrls: response.presignedUrls?.length,
    });
    throw new Error('Failed to fetch all requested presigned URLs');
  }

  return response.presignedUrls.map((presignedUrlResponse): PresignedUrlResult => {
    if (
      !presignedUrlResponse ||
      presignedUrlResponse?.statusInfo?.status === Status.FAILED ||
      !presignedUrlResponse.presignedUrl
    ) {
      const error = presignedUrlResponse?.statusInfo?.errorMessage ?? 'Unknown error';
      logger.error('Failed to fetch presigned URL from control plane', { error });
      throw new Error(`Failed to fetch presigned URL from control plane with error: ${error}`);
    }

    logger.debug('Presigned URL fetched', { presignedUrlResponse });
    return {
      presignedUrl: presignedUrlResponse.presignedUrl,
      key: presignedUrlResponse.key,
    };
  });
};

// Helper type for the getPresignedUrls function to ensure proper typing
type GetPresignedUrlsFunction = (
  client: FireTVCampaignAutomationClient,
  action: PresignedUrlAction,
  keys: string[],
) => Promise<Array<PresignedUrlResult>>;

/**
 * Fetches presigned URLs for multiple S3 keys in a single request.
 * Automatically handles token expiration by refreshing credentials and retrying.
 *
 * @param client - The control plane client instance
 * @param action - The type of access needed ('GET' for viewing, 'PUT' for uploading)
 * @param keys - Array of S3 keys to generate presigned URLs for
 *
 * @returns Array of objects containing original key and its presigned URL
 * @throws Error if URL generation fails
 */
export const getPresignedUrls: GetPresignedUrlsFunction = withTokenRefresh(getPresignedUrlsBase);

/**
 * Implementation of campaign creation initiation logic
 */
const initiateCampaignCreationBase = async (
  client: FireTVCampaignAutomationClient,
  input: CreateCampaignCommandInput,
) => {
  const response = await client.send(new CreateCampaignCommand(input));
  if (!response?.campaignId) {
    throw new Error(String(response));
  }
  return response;
};

// Helper type for campaign creation initiation function with type guards
type InitiateCampaignCreationFunction = (
  client: FireTVCampaignAutomationClient,
  input: CreateCampaignCommandInput,
) => Promise<CreateCampaignCommandOutput>;

/**
 * Initiates a campaign creation process.
 * Automatically handles token expiration by refreshing credentials and retrying.
 *
 * @param client - The control plane client instance
 * @param input - The input data for the campaign creation process
 *
 * @returns The response from the campaign creation process
 * @throws Error if campaign creation fails
 */
export const initiateCampaignCreation: InitiateCampaignCreationFunction = withTokenRefresh(
  initiateCampaignCreationBase,
);

/**
 * Implementation of campaign status fetching logic
 */
const getCampaignStatusBase = async (
  client: FireTVCampaignAutomationClient,
  campaignId: string | undefined,
) => {
  if (!campaignId) {
    throw new Error('Campaign ID is not defined');
  }
  const response = await client.send(
    new GetCampaignCommand({
      campaignId,
    }),
  );
  if (!response?.status) {
    throw new Error(
      `Failed to get campaign status: Missing status field in response. Response data: ${JSON.stringify(response)}`,
    );
  }
  logger.debug('Campaign status', { response });
  return response;
};

// Helper type for the get campaign status function with type guards
type GetCampaignStatusFunction = (
  client: FireTVCampaignAutomationClient,
  campaignId: string | undefined,
) => Promise<GetCampaignCommandOutput>;

/**
 * Fetches the status of a campaign.
 * Automatically handles token expiration by refreshing credentials and retrying.
 *
 * @param client - The control plane client instance
 * @param campaignId - The ID of the campaign to fetch status for
 *
 * @returns The status of the campaign
 * @throws Error if fetching campaign status fails
 */
export const getCampaignStatus: GetCampaignStatusFunction = withTokenRefresh(getCampaignStatusBase);

/**
 * Base implementation for transforming an asset.
 * Sends a transform asset command to the campaign automation service.
 *
 * @param client - The FireTV campaign automation client instance to send requests
 * @param transformAssetInput - Input parameters for the asset transformation
 * @returns Promise resolving to TransformAssetOutput containing transformation details
 * @throws Error if transformation request fails
 */
const transformAssetBase = async (
  client: FireTVCampaignAutomationClient,
  transformAssetInput: TransformAssetInput,
) => {
  return await client.send(new TransformAssetCommand(transformAssetInput));
};

/**
 * Type definition for the transform asset function that handles token refresh
 */
type TransformAssetFunction = (
  client: FireTVCampaignAutomationClient,
  transformAssetInput: TransformAssetInput,
) => Promise<TransformAssetOutput>;

/**
 * Transforms an asset using the campaign automation service.
 * Wraps the base implementation with token refresh handling.
 *
 * @param client - The FireTV campaign automation client instance
 * @param transformAssetInput - Input parameters for the transformation
 * @returns Promise resolving to transformation output
 * @throws Error if transformation fails or token refresh fails
 */
export const transformAsset: TransformAssetFunction = withTokenRefresh(transformAssetBase);

/**
 * Base implementation for retrieving a transformed asset.
 * Gets the status and details of a previously initiated asset transformation.
 *
 * @param client - The FireTV campaign automation client instance to send requests
 * @param transformationId - Unique identifier of the transformation to retrieve
 * @returns Promise resolving to GetTransformedAssetCommandOutput containing asset details
 * @throws Error if retrieval request fails
 */
const getTransformedAssetBase = async (
  client: FireTVCampaignAutomationClient,
  transformationId: string,
) => {
  return await client.send(
    new GetTransformedAssetCommand({
      transformationId,
    }),
  );
};

/**
 * Type definition for the get transformed asset function that handles token refresh
 */
type GetTransformedAssetFunction = (
  client: FireTVCampaignAutomationClient,
  transformationId: string,
) => Promise<GetTransformedAssetCommandOutput>;

/**
 * Retrieves a transformed asset from the campaign automation service.
 * Wraps the base implementation with token refresh handling.
 *
 * @param client - The FireTV campaign automation client instance
 * @param transformationId - ID of the transformation to retrieve
 * @returns Promise resolving to transformed asset details
 * @throws Error if retrieval fails or token refresh fails
 */
export const getTransformedAsset: GetTransformedAssetFunction =
  withTokenRefresh(getTransformedAssetBase);

/**
 * Base implementation for searching a title in catalogue.
 * Sends a search command to the campaign automation service.
 *
 * @param client - The FireTV campaign automation client instance to send requests
 * @param searchInput - Input parameters for search
 * @returns Promise resolving to SearchOutput containing results of the search
 * @throws Error if search request fails
 */
const searchBase = async (client: FireTVCampaignAutomationClient, searchInput: SearchInput) => {
  return await client.send(new SearchCommand(searchInput));
};

/**
 * Type definition for the search function that handles token refresh
 */
type SearchFunction = (
  client: FireTVCampaignAutomationClient,
  searchInput: SearchInput,
) => Promise<SearchOutput>;

/**
 * Searches for a title in the catalogue using the campaign automation service.
 * Wraps the base implementation with token refresh handling.
 *
 * @param client - The FireTV campaign automation client instance
 * @param searchInput - Input parameters for the searching the catalogue
 * @returns Promise resolving to search output
 * @throws Error if search fails or token refresh fails
 */
export const search: SearchFunction = withTokenRefresh(searchBase);

// Add additional API methods here using the same pattern:
// 1. Create base method that focuses on core functionality
// 2. Wrap it with withTokenRefresh to add token handling
